import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useMyFees } from "@/hooks/useMyFees";
import type { MyFee } from "@/types/profile.types";
import { MyFeeStatusBadge } from "./MyFeeStatusBadge";
import { MyFeeDetailPage } from "./MyFeeDetailPage";
import { BackHeader, Card, Chip, ProgressBar, Button, Icon, ICONS } from "@/components/kit";

interface MyApplicationsPageProps {
  onBack: () => void;
  /** Bo'sh holatda "Ariza topshirish" tugmasi uchun (ixtiyoriy) */
  onCreate?: () => void;
}

function FeeCard({ fee, index, onOpen }: { fee: MyFee; index: number; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const collected = parseFloat(fee.collected) || 0;
  const amount = parseFloat(fee.amount) || 0;
  const progress = amount > 0 ? Math.min(Math.round((collected / amount) * 100), 100) : 0;
  const hasProgress = amount > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.05 }}>
      <Card
        onClick={() => onOpen(fee.id)}
        role="button"
        tabIndex={0}
        className="p-4 transition-transform active:scale-[0.99]"
        style={{ cursor: "pointer" }}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-base font-bold" style={{ color: "var(--c-text)" }}>
            {t("applicationNo")}: #{fee.id}
          </p>
          <MyFeeStatusBadge status={fee.status} statusId={fee.statusId} />
        </div>
        {fee.createdAt && (
          <p className="mt-0.5 text-xs" style={{ color: "var(--c-text-muted)" }}>
            {fee.createdAt}
          </p>
        )}
        <p className="mt-2 text-base font-semibold" style={{ color: "var(--c-text)" }}>
          {fee.patientName} {fee.patientSurname}
        </p>

        {hasProgress && (
          <div className="mt-3">
            <div className="mb-1.5 flex items-center justify-between text-xs font-medium">
              <span style={{ color: "var(--c-text-muted)" }}>{t("myFeesCollected")}:</span>
              <span style={{ color: "var(--c-text)" }}>{progress}%</span>
            </div>
            <ProgressBar value={progress} height={6} />
            <div className="mt-2 flex items-center justify-between text-xs font-medium">
              <span style={{ color: "var(--c-text-muted)" }}>
                {t("overallGoal")}: {fee.amount}
              </span>
              <span style={{ color: "var(--c-text)" }}>{fee.collected}</span>
            </div>
          </div>
        )}
      </Card>
    </motion.div>
  );
}

export function MyApplicationsPage({ onBack, onCreate }: MyApplicationsPageProps) {
  const { t } = useTranslation();
  const { fees, isLoading } = useMyFees();
  const [selectedFeeId, setSelectedFeeId] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("barchasi");

  const statuses = useMemo(
    () => Array.from(new Set(fees.map((f) => f.status).filter(Boolean))),
    [fees],
  );
  const filtered = filter === "barchasi" ? fees : fees.filter((f) => f.status === filter);

  if (selectedFeeId !== null) {
    return <MyFeeDetailPage feeId={selectedFeeId} onBack={() => setSelectedFeeId(null)} />;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
      <div className="mx-auto max-w-lg">
        <BackHeader onBack={onBack} />

        <div className="px-4 pb-10">
          <h1 className="text-2xl font-bold" style={{ color: "var(--c-text)" }}>
            {t("myFeesTitle")}
          </h1>

          {isLoading ? (
            <LoadingScreen className="min-h-[40vh]" />
          ) : fees.length === 0 ? (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
              <span style={{ color: "var(--c-text-tertiary)" }}>
                <Icon name={ICONS.empty} size={48} />
              </span>
              <p className="text-sm font-medium" style={{ color: "var(--c-text-muted)" }}>
                {t("myFeesEmpty")}
              </p>
              {onCreate && (
                <Button onClick={onCreate}>{t("profileCreateApp")}</Button>
              )}
            </div>
          ) : (
            <>
              {statuses.length > 0 && (
                <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1" style={{ scrollbarWidth: "none" }}>
                  <Chip selected={filter === "barchasi"} onClick={() => setFilter("barchasi")}>
                    {t("filterAll")}
                  </Chip>
                  {statuses.map((s) => (
                    <Chip key={s} selected={filter === s} onClick={() => setFilter(s)}>
                      {s}
                    </Chip>
                  ))}
                </div>
              )}

              <div className="mt-3 flex flex-col gap-3">
                {filtered.map((fee, i) => (
                  <FeeCard key={fee.id} fee={fee} index={i} onOpen={setSelectedFeeId} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
